import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useState,
  useRef,
  type ReactElement,
  type ReactNode,
} from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { procitajGreske } from "@/lib/greske";
import {
  siteEquipmentApi,
  type EquipmentCondition,
  type EquipmentEvent,
  type EquipmentOwnership,
  type EquipmentPlace,
  type EquipmentResult,
  type EquipmentUndoSummary,
} from "@/lib/api/oprema";
import { datum } from "@/lib/format";
import "./site-equipment.css";
import { EquipmentDocumentActions } from "./EquipmentDocumentActions";
import { EquipmentPhotoLink } from "./EquipmentPhotoLink";

export function EquipmentRouteButton({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  const navigate = useNavigate();
  return (
    <Button
      type="button"
      variant="outline"
      className={className}
      onClick={() => navigate(to)}
    >
      {children}
    </Button>
  );
}

export function useEquipmentAccess() {
  const { can, user } = useAuth();
  const read = can("oprema_gradiliste", "read");
  const granted = (action: string, scopes = ["team", "all"]) =>
    read &&
    (user?.permissions?.some(
      (p) =>
        p.module === "oprema_gradiliste" &&
        p.action === action &&
        scopes.includes(p.scope),
    ) ??
      false);
  const all = granted("update", ["all"]);
  const create = granted("create");
  const update = granted("update");
  return {
    read,
    all,
    approve: granted("approve", ["all"]),
    catalogue: all && update,
    evidenceException: all && update,
    move: update,
    damage: create,
    receive: create,
    maintain: update,
    canSend: (place: EquipmentPlace) =>
      update && (place.vrsta === "gradiliste" || all),
    canMaintain: (place: EquipmentPlace) =>
      update && (place.vrsta === "gradiliste" || all),
  };
}
export function Ownership({ item }: { item: EquipmentOwnership }) {
  return item.najam_id ? (
    <Badge tone="info">
      Najam · {item.najmodavac || "Najmodavac"}
      {item.povrat_do ? ` do ${datum(item.povrat_do)}` : ""}
    </Badge>
  ) : null;
}
export function PlaceStatus({ place }: { place: EquipmentPlace }) {
  if (!place.popisano_at) return <Badge tone="neutral">Nije popisano</Badge>;
  return (
    <Badge tone={place.inventura_istekla ? "warn" : "ok"}>
      {place.inventura_istekla
        ? "Inventura istekla"
        : `Popisano ${datum(place.popisano_at)}`}
    </Badge>
  );
}
export const conditionNames: Record<EquipmentCondition, string> = {
  ispravno: "Ispravno",
  osteceno: "Oštećeno",
  u_radioni: "U radioni",
  u_prijevozu: "U prijevozu",
  otpisano: "Otpisano",
  nestalo: "Nestalo",
  vraceno_najmodavcu: "Vraćeno najmodavcu",
};
export function Condition({ value }: { value: EquipmentCondition }) {
  return (
    <Badge
      tone={
        value === "ispravno"
          ? "ok"
          : ["osteceno", "nestalo", "otpisano"].includes(value)
            ? "bad"
            : value === "u_radioni"
              ? "warn"
              : "info"
      }
    >
      {conditionNames[value]}
    </Badge>
  );
}
export function PendingWriteOff({ quantity }: { quantity?: number | boolean }) {
  return quantity ? (
    <Badge tone="warn">
      {typeof quantity === "number" ? `${quantity} ` : ""}čeka otpis
    </Badge>
  ) : null;
}
export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  const id = useId();
  const input =
    isValidElement(children) &&
    ["input", "select", "textarea"].includes(String(children.type));
  return (
    <div className="equipment-field">
      {input ? (
        <label htmlFor={id}>{label}</label>
      ) : (
        <span id={`${id}-label`}>{label}</span>
      )}
      {input ? (
        cloneElement(children as ReactElement<Record<string, unknown>>, {
          id,
          "aria-invalid": !!error,
          "aria-describedby": error ? `${id}-error` : undefined,
        })
      ) : (
        <div role="group" aria-labelledby={`${id}-label`}>
          {children}
        </div>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="text-red-800">
          {error}
        </p>
      )}
    </div>
  );
}
export function Quantity({
  label,
  value,
  onChange,
  max,
  min = 0,
  error,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  max?: number;
  min?: number;
  error?: string;
}) {
  const id = useId();
  return (
    <div>
      <div className="equipment-quantity">
        <Button
          type="button"
          variant="outline"
          aria-label={`Manje: ${label}`}
          disabled={value <= min}
          onClick={() => onChange(value - 1)}
        >
          −
        </Button>
        <input
          aria-label={label}
          aria-invalid={!!error}
          aria-describedby={error ? id : undefined}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          step={1}
          value={Number.isNaN(value) ? "" : value}
          onChange={(e) =>
            onChange(e.target.value === "" ? NaN : Number(e.target.value))
          }
        />
        <Button
          type="button"
          variant="outline"
          aria-label={`Više: ${label}`}
          disabled={max !== undefined && value >= max}
          onClick={() => onChange(value + 1)}
        >
          +
        </Button>
      </div>
      {error && (
        <p className="equipment-error" role="alert" id={id}>
          {error}
        </p>
      )}
    </div>
  );
}
export function Photos({
  files,
  onChange,
  required,
  error,
  label = "Slikaj opremu",
}: {
  files: File[];
  onChange: (files: File[]) => void;
  required?: boolean;
  error?: string;
  label?: string;
}) {
  const errorId = useId();
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="equipment-photo">
      <Button
        type="button"
        variant="outline"
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        className={
          files.length ? "equipment-proof-ready" : "equipment-photo-button"
        }
        onClick={() => input.current?.click()}
      >
        {files.length
          ? "✓ Fotka dodana · Promijeni"
          : `${label}${required ? " (obavezno)" : ""}`}
      </Button>
      <input
        ref={input}
        hidden
        aria-label="Fotke"
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        type="file"
        accept="image/jpeg,image/png"
        multiple
        onChange={(e) => onChange(Array.from(e.target.files ?? []))}
      />
      {files.map((file, index) => (
        <span key={`${file.name}-${index}`}>{file.name}</span>
      ))}
      {required && !files.length && <p>Dodaj fotku prije spremanja.</p>}
      {error && (
        <p role="alert" id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}
export function useEquipmentSave(
  onSaved: (result: EquipmentResult, message: string) => void,
) {
  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  return {
    pending,
    errors,
    error,
    setErrors,
    run: async (task: () => Promise<EquipmentResult>, message: string) => {
      if (pending) return;
      setPending(true);
      setErrors({});
      setError("");
      try {
        const result = await task();
        onSaved(result, message);
      } catch (cause) {
        const parsed = procitajGreske(cause);
        setErrors(parsed.polja);
        setError(parsed.opcenito.join(" "));
      } finally {
        setPending(false);
      }
    },
  };
}
export function SaveErrors({
  error,
  errors,
  inline = [],
}: {
  error: string;
  errors: Record<string, string>;
  inline?: string[];
}) {
  return (
    <>
      {error && (
        <p role="alert" className="equipment-error">
          {error}
        </p>
      )}
      {Object.entries(errors)
        .filter(([key]) => !inline.includes(key))
        .map(([key, message]) => (
          <p role="alert" className="equipment-error" key={key}>
            {message}
          </p>
        ))}
    </>
  );
}
export function UndoNotice({
  operation,
  onUndone,
}: {
  operation: { result: EquipmentResult; message: string } | null;
  onUndone: () => void;
}) {
  const [now, setNow] = useState(Date.now);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [undoResult, setUndoResult] = useState<{
    id: string;
    summary: EquipmentUndoSummary;
  } | null>(null);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = operation
    ? Math.max(
        0,
        Math.min(
          120,
          Math.ceil(
            (new Date(operation.result.ponistivo_do).getTime() - now) / 1000,
          ),
        ),
      )
    : 0;
  if (!operation) return null;
  const summary =
    undoResult?.id === operation.result.id ? undoResult.summary : null;
  return (
    <div className="equipment-undo" role="status">
      <span>{operation.message}</span>
      {operation.result.poslao && <p>Predao {operation.result.poslao}</p>}
      {operation.result.primio && <p>Primio {operation.result.primio}</p>}
      {operation.result.bez_potpisa_razlog && (
        <p className="equipment-error">
          Bez potpisa · {operation.result.bez_potpisa_razlog}
        </p>
      )}
      {operation.result.bez_fotke_razlog && (
        <p className="equipment-error">
          Bez fotke · {operation.result.bez_fotke_razlog}
        </p>
      )}
      {operation.result.dokument && (
        <EquipmentDocumentActions document={operation.result.dokument} />
      )}
      {summary ? (
        <div>
          <p>
            Poništeno {summary.ponisteno.length} od {summary.ukupno}
          </p>
          {!!summary.neuspjelo.length && (
            <ul aria-label="Neuspjela poništenja">
              {summary.neuspjelo.map((item) => (
                <li key={item.id}>
                  Stavka uvoza{" "}
                  {(operation.result.ponisti_ids?.indexOf(item.id) ?? 0) + 1}:{" "}
                  {item.razlog}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : seconds > 0 ? (
        <Button
          variant="outline"
          disabled={pending}
          onClick={() => {
            setPending(true);
            setError("");
            void siteEquipmentApi
              .undo(operation.result.id, operation.result.ponisti_ids)
              .then((result) => {
                if (result)
                  setUndoResult({ id: operation.result.id, summary: result });
                else onUndone();
              })
              .catch((cause) =>
                setError(
                  [
                    ...procitajGreske(cause).opcenito,
                    ...Object.values(procitajGreske(cause).polja),
                  ].join(" ") ||
                    "Poništenje nije uspjelo. Ponovno učitajte stanje.",
                ),
              )
              .finally(() => setPending(false));
          }}
        >
          Poništi ({seconds} s)
        </Button>
      ) : (
        <span>Rok za poništenje je istekao.</span>
      )}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
const eventNames: Record<string, string> = {
  pocetno: "Početni unos",
  uvoz: "Uvoz",
  selidba_poslano: "Preseljeno – čeka primitak",
  zaduzenje: "Zaduženo iz Baze – čeka primitak",
  povrat: "Povrat u Bazu – čeka primitak",
  selidba_primljeno: "Primljeno",
  ostecenje: "Oštećenje",
  u_radionu: "Pošalji u radionu",
  iz_radione: "Vraćeno iz radione",
  produljenje_roka: "Produlji rok",
  otpis: "Otpis",
  nestalo: "Nestalo",
  pronadeno: "Pronađeno",
  ponisteno: "Poništeno",
};
export function History({
  events,
  places,
}: {
  events: EquipmentEvent[];
  places: EquipmentPlace[];
}) {
  return (
    <ol className="equipment-history">
      {events.map((event) => (
        <li
          key={event.id}
          className={
            event.vrsta === "ostecenje" ||
            event.vrsta === "otpis" ||
            event.vrsta === "nestalo"
              ? "is-bad"
              : event.vrsta === "selidba_primljeno" ||
                  event.vrsta === "pronadeno"
                ? "is-ok"
                : "is-info"
          }
        >
          <b>
            {datum(event.datum)} · {eventNames[event.vrsta] ?? event.vrsta}
          </b>
          <p>
            {event.korisnik} · {event.kolicina} kom
          </p>
          <p>
            {places.find((p) => p.id === event.iz_mjesta_id)?.naziv}
            {event.u_mjesto_id
              ? ` → ${places.find((p) => p.id === event.u_mjesto_id)?.naziv ?? "Mjesto"}`
              : ""}
          </p>
          {event.razlog && <p>{event.razlog}</p>}
          {event.komad_id && (
            <EquipmentRouteButton
              className="equipment-link"
              to={`/oprema/komadi/${event.komad_id}`}
            >
              Otvori komad
            </EquipmentRouteButton>
          )}
          {event.bez_potpisa_razlog && (
            <p className="equipment-error">
              Bez potpisa · {event.bez_potpisa_razlog}
            </p>
          )}
          {event.bez_fotke_razlog && (
            <p className="equipment-error">
              Bez fotke · {event.bez_fotke_razlog}
            </p>
          )}
          {event.dokument && (
            <EquipmentDocumentActions document={event.dokument} />
          )}
          {event.status === "ceka_potvrdu" && (
            <Badge tone="warn">Čeka potvrdu uprave</Badge>
          )}
          {event.status === "odbijeno" && (
            <Badge tone="neutral">Odbijeno</Badge>
          )}
          {event.status === "ponisteno" && (
            <Badge tone="neutral">Poništeno</Badge>
          )}
          {event.fotke.map((photo) => (
            <EquipmentPhotoLink
              key={photo.id}
              id={photo.id}
              label={photo.naziv}
            />
          ))}
        </li>
      ))}
      {!events.length && <li>Nema zabilježenih kretanja.</li>}
    </ol>
  );
}
